/**
 * Task Status API service for managing task status configurations.
 * @module lib/api/task-status.api
 */

import { apiClient } from './api-client';
import { createSingletonApi } from './singleton-factory';
import type { StatusConfig, StatusMigrationPreview, StatusCustomMapping } from '@scrambl/shared';

export interface TaskStatusResponse {
  statuses: StatusConfig[];
  source:
    | 'project-type-custom'
    | 'task-type-custom'
    | 'group-custom'
    | 'project-custom'
    | 'organization-default'
    | 'system-default';
}

/** Where a project task type's statuses come from. */
export type ProjectTypeStatusSource = 'ORG_DEFAULT' | 'PROJECT_DEFAULT' | 'CUSTOM';

export interface ProjectTypeStatusConfig {
  statusSource: ProjectTypeStatusSource;
  /** The statuses currently in effect for tasks of this type in this project. */
  effectiveStatuses: StatusConfig[];
  effectiveSource: TaskStatusResponse['source'];
  /** The project's custom set for the type — possibly dormant (source ≠ CUSTOM). */
  customStatuses: StatusConfig[];
  /** The org's default set for the type (empty when the type has none). */
  orgDefaultStatuses: StatusConfig[];
}

export interface CreateTaskStatusDto {
  organizationId: string;
  moduleType: 'ORGANIZATION' | 'PROJECT' | 'TASK_GROUP';
  moduleId?: string;
  projectType?: string;
  statusId: string;
  name: string;
  color: string;
  statusTypeId: string;
  order: number;
  isDefault?: boolean;
  wipLimit?: number;
  description?: string;
}

export interface UpdateTaskStatusDto {
  organizationId: string;
  moduleType: 'ORGANIZATION' | 'PROJECT' | 'TASK_GROUP';
  moduleId?: string;
  projectType?: string;
  statuses: StatusConfig[];
}

export interface HasCustomStatusesDto {
  organizationId: string;
  moduleType: 'PROJECT' | 'TASK_GROUP';
  moduleId: string;
}

class TaskStatusApiService {
  private baseUrl = '/task-status';

  /**
   * Preview status migration before applying changes
   * Returns mappings and affected task counts without making changes
   */
  async previewStatusMigration(
    organizationId: string,
    moduleType: 'PROJECT' | 'TASK_GROUP' | 'ORG_TASK_TYPE',
    moduleId: string | null,
    oldStatuses: StatusConfig[],
    newStatuses: StatusConfig[],
    taskTypeDefId?: string
  ): Promise<StatusMigrationPreview> {
    return apiClient<StatusMigrationPreview>(`${this.baseUrl}/preview-migration`, {
      method: 'POST',
      body: {
        organizationId,
        moduleType,
        moduleId,
        oldStatuses,
        newStatuses,
        taskTypeDefId,
      },
    });
  }

  /**
   * Gets organization default statuses for a project type
   */
  async getOrganizationDefaults(
    organizationId: string,
    projectType: string
  ): Promise<StatusConfig[]> {
    const response = await apiClient<TaskStatusResponse>(
      `${this.baseUrl}/organization/${organizationId}/${projectType}`,
      { method: 'GET' }
    );
    return response.statuses;
  }

  /**
   * Sets organization default statuses for a project type
   */
  async setOrganizationDefaults(
    organizationId: string,
    projectType: string,
    statuses: StatusConfig[]
  ): Promise<void> {
    await apiClient(`${this.baseUrl}/organization/${organizationId}/${projectType}`, {
      method: 'POST',
      body: { statuses },
    });
  }

  /**
   * Resets organization defaults to system defaults
   */
  async resetOrganizationDefaults(organizationId: string, projectType: string): Promise<void> {
    await apiClient(`${this.baseUrl}/organization/${organizationId}/${projectType}`, {
      method: 'DELETE',
    });
  }

  /**
   * Gets project custom statuses
   */
  async getProjectStatuses(
    organizationId: string,
    projectId: string
  ): Promise<TaskStatusResponse> {
    return apiClient<TaskStatusResponse>(
      `${this.baseUrl}/project/${organizationId}/${projectId}`,
      { method: 'GET' }
    );
  }

  /**
   * Aggregates every status a project can show across ALL of its task types
   * (union of each type's resolved set plus the base project chain, deduped by
   * id). Backs the Tasks page Status filter so the list is complete regardless
   * of which task types are currently loaded on screen. `statusTaskTypeIds`
   * maps each status id to the task types whose resolved set contains it.
   */
  async getProjectAllTypeStatuses(
    organizationId: string,
    projectId: string
  ): Promise<{
    statuses: StatusConfig[];
    statusTaskTypeIds?: Record<string, string[]>;
  }> {
    return apiClient<{
      statuses: StatusConfig[];
      statusTaskTypeIds?: Record<string, string[]>;
    }>(
      `${this.baseUrl}/project-all-types/${organizationId}/${projectId}`,
      { method: 'GET' }
    );
  }

  /**
   * Resolves the statuses available to a task in a single call, honoring the
   * full hierarchy (task group → project → organization → system defaults).
   * Pass groupId to factor in a task group's custom statuses.
   */
  async resolveTaskStatuses(
    organizationId: string,
    projectId: string,
    groupId?: string,
    taskTypeDefId?: string
  ): Promise<TaskStatusResponse> {
    const params = new URLSearchParams();
    if (groupId) params.set('groupId', groupId);
    if (taskTypeDefId) params.set('taskTypeDefId', taskTypeDefId);
    const query = params.size > 0 ? `?${params.toString()}` : '';
    return apiClient<TaskStatusResponse>(
      `${this.baseUrl}/resolve/${organizationId}/${projectId}${query}`,
      { method: 'GET' }
    );
  }

  /**
   * Sets project custom statuses with smart task migration
   */
  async setProjectStatuses(
    organizationId: string,
    projectId: string,
    statuses: StatusConfig[],
    options?: {
      migrateExistingTasks?: boolean;
      customMappings?: StatusCustomMapping[];
    }
  ): Promise<{
    count: number;
    migration?: {
      migratedTasks: number;
      skippedTasks: number;
      fallbackUsed: boolean;
    };
  }> {
    return apiClient(`${this.baseUrl}/project/${organizationId}/${projectId}`, {
      method: 'POST',
      body: {
        statuses,
        migrateExistingTasks: options?.migrateExistingTasks ?? true,
        customMappings: options?.customMappings,
      },
    });
  }

  /**
   * Resets project statuses (will inherit from organization or system defaults)
   */
  async resetProjectStatuses(organizationId: string, projectId: string): Promise<void> {
    await apiClient(`${this.baseUrl}/project/${organizationId}/${projectId}`, {
      method: 'DELETE',
    });
  }

  /**
   * Gets a task type's status configuration within a project: its status
   * source, the statuses in effect, the (possibly dormant) custom set, and
   * the org default set.
   */
  async getProjectTypeStatuses(
    organizationId: string,
    projectId: string,
    taskTypeDefId: string
  ): Promise<ProjectTypeStatusConfig> {
    return apiClient<ProjectTypeStatusConfig>(
      `${this.baseUrl}/project/${organizationId}/${projectId}/type/${taskTypeDefId}`,
      { method: 'GET' }
    );
  }

  /**
   * Sets a task type's status source within a project (and, for CUSTOM, its
   * status set), remapping the project's tasks of that type unless
   * migrateExistingTasks is false ("update later").
   */
  async setProjectTypeStatusSource(
    organizationId: string,
    projectId: string,
    taskTypeDefId: string,
    body: {
      statusSource: ProjectTypeStatusSource;
      statuses?: StatusConfig[];
      migrateExistingTasks?: boolean;
      customMappings?: StatusCustomMapping[];
    }
  ): Promise<{
    statusSource: ProjectTypeStatusSource;
    statuses: StatusConfig[];
    source: TaskStatusResponse['source'];
    migration?: {
      migratedTasks: number;
      skippedTasks: number;
      fallbackUsed: boolean;
    };
  }> {
    return apiClient(
      `${this.baseUrl}/project/${organizationId}/${projectId}/type/${taskTypeDefId}`,
      { method: 'PUT', body }
    );
  }

  /**
   * @deprecated Task groups no longer own statuses; tasks inherit their workflow
   * from their task type. Returns an empty set so legacy callers keep working
   * without hitting a removed endpoint.
   */
  async getTaskGroupStatuses(
    _organizationId: string,
    _groupId: string
  ): Promise<TaskStatusResponse> {
    return { statuses: [], source: 'project-custom' };
  }

  /**
   * Gets system default statuses for a project type
   */
  async getSystemDefaults(projectType: string): Promise<StatusConfig[]> {
    return apiClient<StatusConfig[]>(`${this.baseUrl}/defaults/${projectType}`, {
      method: 'GET',
    });
  }

  /**
   * Checks if a module has custom statuses
   */
  async hasCustomStatuses(
    organizationId: string,
    moduleType: 'PROJECT' | 'TASK_GROUP',
    moduleId: string
  ): Promise<boolean> {
    const response = await apiClient<{ hasCustom: boolean }>(
      `${this.baseUrl}/has-custom/${organizationId}/${moduleType}?moduleId=${moduleId}`,
      { method: 'GET' }
    );
    return response.hasCustom;
  }

  /**
   * Gets all project types that have organization defaults
   */
  async getOrganizationProjectTypes(organizationId: string): Promise<string[]> {
    return apiClient<string[]>(
      `${this.baseUrl}/organization/${organizationId}/project-types`,
      { method: 'GET' }
    );
  }
}

export const taskStatusApi = createSingletonApi(TaskStatusApiService);
